// The public API document, its versions and its changelog.
//
// Hand-typed, like `status`, because these routes are served by the gateway
// itself and the gateway publishes no OpenAPI document of its own. There is
// nothing to generate from.
//
// Every call here needs a signed-in customer session. An API key presented to
// these paths is refused with `NOT_PUBLIC_API`: the key's own surface is what
// the document describes, and the document is not part of it.
//
// ── A note on keys and this SDK ──────────────────────────────────────────────
// This package runs in a browser. It therefore has no API-key mode and will not
// grow one: a key in a page is a key every visitor can read, which is exactly
// what the document it fetches tells developers not to do. Use the Python SDK,
// or plain HTTP with `X-Api-Key`, from your own server.

import { http } from './_core/request.js';
import type { Envelope } from './_core/envelope.js';

/** One scope a key may hold, as the document publishes it. */
export interface PublicApiScope {
  name: string;
  description: string;
}

/**
 * The public API document. Typed loosely on purpose: it is an OpenAPI 3.1
 * document, its shape is the OpenAPI specification's business rather than
 * ours, and pinning it here would be a second, weaker copy of that standard.
 * The fields named below are the ones this platform adds or relies on.
 */
export interface PublicApiDocument {
  openapi: string;
  info: {
    title: string;
    version: string;
    summary?: string;
    description?: string;
    /** `YYYY-MM-DD`: the day this version of the document was published. */
    'x-rr-released-on'?: string;
  };
  servers?: { url: string; description?: string }[];
  paths: Record<string, Record<string, PublicApiOperation>>;
  webhooks?: Record<string, Record<string, PublicApiOperation>>;
  components?: { schemas?: Record<string, unknown>; securitySchemes?: Record<string, unknown> };
  tags?: { name: string; description?: string }[];
  /** The scope vocabulary, so a reader can see the whole table at once. */
  'x-rr-scopes'?: PublicApiScope[];
  /** Present only when an operation the catalogue names had no source document. */
  'x-rr-missing'?: { method: string; path: string; reason: string }[];
  [key: string]: unknown;
}

/** One operation, with the platform's own annotations alongside the OpenAPI fields. */
export interface PublicApiOperation {
  summary?: string;
  description?: string;
  operationId?: string;
  tags?: string[];
  deprecated?: boolean;
  parameters?: PublicApiParameter[];
  requestBody?: {
    required?: boolean;
    content?: Record<string, { schema?: unknown; example?: unknown }>;
  };
  responses?: Record<string, { description?: string; content?: Record<string, { schema?: unknown }> }>;
  security?: Record<string, string[]>[];
  /** The one scope a key must hold to call this operation. */
  'x-rr-scope'?: string;
  /** Why that scope, where the answer is not obvious. */
  'x-rr-scope-note'?: string;
  [key: string]: unknown;
}

export interface PublicApiParameter {
  name: string;
  in: 'path' | 'query' | 'header' | 'cookie';
  required?: boolean;
  description?: string;
  schema?: { type?: string | string[]; format?: string; enum?: unknown[]; default?: unknown };
}

/** One published version of the document. */
export interface PublicApiVersion {
  version: string;
  released_on?: string | null;
  /** True for the version being served now. */
  current: boolean;
  /**
   * True when the document is assembled per request from the live services,
   * which only the current version is. Older versions are frozen copies.
   */
  live: boolean;
  /** Path to fetch this version, relative to the API base. */
  url: string;
}

/** What changed in one release. */
export interface PublicApiRelease {
  version: string;
  released_on: string;
  summary: string;
  changes: { kind: 'added' | 'changed' | 'fixed' | 'deprecated' | string; text: string }[];
}

/** The current document, assembled from the services' own OpenAPI documents. */
export function getOpenApi(): Promise<PublicApiDocument> {
  return http.get<PublicApiDocument>('/v1/developer/openapi.json');
}

/** Every published version, current first. */
export function listVersions(): Promise<Envelope<PublicApiVersion[]>> {
  return http.get<Envelope<PublicApiVersion[]>>('/v1/developer/versions');
}

/** One previously published version, exactly as it was published. */
export function getVersion(version: string): Promise<PublicApiDocument> {
  return http.get<PublicApiDocument>(`/v1/developer/openapi/${encodeURIComponent(version)}.json`);
}

/** What was added or changed in each version, newest first. */
export function getChangelog(): Promise<Envelope<PublicApiRelease[]>> {
  return http.get<Envelope<PublicApiRelease[]>>('/v1/developer/changelog');
}
